import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';

import TextInput from '../../../TextInput';
import SelectCustom from '../../../SelectCustom';
import ErrorBlock from '../../../common/ErrorBlock';
import DatePickerCustom from '../../../common/DatePickerCustom';

import useCountryData from './hooks/useCountryData';
import {
  adaptInitialValue,
  adaptInitialValueState,
} from '../../HomeAddress/utils';
import useGenderOptions from '../../../../hooks/selectOptions/useGenderOptions';
import useInsuranceRelationOptions from '../../../../hooks/selectOptions/useInsuranceRelationOptions';

import { LENGTH_VALUES } from '../../../../constants/validations';
import { INSURANCE_ELIGIBILITY_FIELDS_TYPES } from '../../../../constants/formFields';
import {
  DATE_PICKER_DATE_FORMAT,
  DATE_PICKER_TODAY_MAX_DATE,
} from '../../../../constants/dates';
import {
  getDateFieldValue,
  getTimeFromDateUTC,
} from '../../../PatientHealthboard/utils';
import {
  getFieldProps,
  FIELD_FILLING_TYPES,
} from '../../../../utils/fields/getFieldProps';

import styles from './index.styl';

const {
  DOB,
  LAST_NAME,
  FIRST_NAME,
  PHONE_NUMBER,
  ADDRESS_1,
  ADDRESS_2,
  CITY,
  STATE,
  POSTAL_CODE,
  GENDER,
  INSURANCE_RELATION_ID,
} = INSURANCE_ELIGIBILITY_FIELDS_TYPES;

const resetCommonFieldProps = {
  className: '',
};

const PrimarySubscriberFormSection = ({
  handleFormChange,
  formState,
  formErrors,
  handleChangeField,
}) => {
  const { formatMessage } = useIntl();

  const datePickerLabel = formatMessage({ id: 'profile.fields.dob' });
  const phoneNumberLabel = formatMessage({ id: 'profile.fields.phone' });
  const insuranceRelationFieldLabel = formatMessage({
    id: 'profile.fields.relationship',
  });

  const makeField = getFieldProps(formatMessage, handleFormChange);

  const { genderOptions } = useGenderOptions(formatMessage);

  const {
    insuranceRelationOptions,
    hideInsuranceRelationFieldOrInfo,
  } = useInsuranceRelationOptions();

  const { stateList, zipLimit, isZipApplicable } = useCountryData();

  const dateFieldValue = formState[DOB]
    ? getDateFieldValue(formState[DOB])
    : undefined;

  const safeState = adaptInitialValueState(formState[STATE], stateList);
  const safeGender = adaptInitialValue(formState[GENDER], genderOptions);
  const safeRelation = adaptInitialValue(
    formState[INSURANCE_RELATION_ID],
    insuranceRelationOptions,
  );

  const handleChangeState = data => {
    handleFormChange({ state: data });
  };

  const handleRelationChange = data => {
    handleFormChange({ insuranceRelationId: data.value });
  };

  const handleDobChange = value => {
    let date = value;

    if (typeof value === 'object' && value !== null) {
      date = getTimeFromDateUTC(value, DATE_PICKER_DATE_FORMAT, false);
    }

    handleChangeField(DOB)(date);
  };

  return (
    <section className={styles.section}>
      <TextInput
        {...makeField(FIRST_NAME)}
        {...resetCommonFieldProps}
        withTrimMode
        required={false}
        value={formState[FIRST_NAME]}
      />

      <TextInput
        {...makeField(LAST_NAME)}
        {...resetCommonFieldProps}
        withTrimMode
        required={false}
        value={formState[LAST_NAME]}
      />

      <div>
        <DatePickerCustom
          {...makeField(DOB)}
          {...resetCommonFieldProps}
          iconClassName={styles.datePickerIcon}
          name={DOB}
          maxDate={DATE_PICKER_TODAY_MAX_DATE}
          formatDate={DATE_PICKER_DATE_FORMAT}
          value={dateFieldValue}
          label={datePickerLabel}
          onChange={handleDobChange}
          withManualInputDob
        />
        {formErrors.dob && (
          <ErrorBlock errors={formErrors.dob} className={styles.error} />
        )}
      </div>

      <div>
        <TextInput
          {...resetCommonFieldProps}
          label={phoneNumberLabel}
          onChange={handleChangeField(PHONE_NUMBER)}
          placeholder={phoneNumberLabel}
          type="number"
          name={PHONE_NUMBER}
          value={formState[PHONE_NUMBER]}
          required={false}
          limit={LENGTH_VALUES.HOME_PHONE.MAX}
        />
        {formErrors.phoneNumber && (
          <ErrorBlock
            errors={formErrors.phoneNumber}
            className={styles.error}
          />
        )}
      </div>

      <SelectCustom
        {...makeField(GENDER)}
        className={styles.selectField}
        options={genderOptions}
        needUpdateDefaultValue
        required={false}
        value={safeGender}
      />

      {!hideInsuranceRelationFieldOrInfo && (
        <SelectCustom
          {...makeField(INSURANCE_RELATION_ID)}
          className={styles.selectField}
          onChange={handleRelationChange}
          options={insuranceRelationOptions}
          placeholder={insuranceRelationFieldLabel}
          label={insuranceRelationFieldLabel}
          needUpdateDefaultValue
          required={false}
          value={safeRelation}
        />
      )}

      <TextInput
        {...makeField(ADDRESS_1)}
        {...resetCommonFieldProps}
        value={formState[ADDRESS_1]}
        required={false}
      />

      <TextInput
        {...makeField(ADDRESS_2, FIELD_FILLING_TYPES.OPTIONAL)}
        {...resetCommonFieldProps}
        value={formState[ADDRESS_2]}
        required={false}
      />

      <TextInput
        {...makeField(CITY)}
        {...resetCommonFieldProps}
        value={formState[CITY]}
        required={false}
      />

      <SelectCustom
        {...makeField(STATE)}
        className={styles.selectField}
        onChange={handleChangeState}
        value={safeState}
        options={stateList}
        needUpdateDefaultValue
        required={false}
      />

      <div>
        <TextInput
          {...makeField(POSTAL_CODE)}
          {...resetCommonFieldProps}
          limit={zipLimit}
          value={formState[POSTAL_CODE]}
          disabled={!isZipApplicable}
          required={false}
          onChange={handleChangeField(POSTAL_CODE)}
        />
        {formErrors.postalCode && (
          <ErrorBlock
            errors={[formErrors.postalCode]}
            className={styles.error}
          />
        )}
      </div>
    </section>
  );
};

PrimarySubscriberFormSection.propTypes = {
  handleFormChange: PropTypes.func.isRequired,
  formState: PropTypes.object.isRequired,
  formErrors: PropTypes.object.isRequired,
  handleChangeField: PropTypes.func.isRequired,
};

export default memo(PrimarySubscriberFormSection);



// WEBPACK FOOTER //
// ./src/components/ProfileLayout/InsuranceDetails/PrimarySubscriberFormSection/index.jsx